import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Calendar, Plus, FileText, Users, CheckCircle, Clock, 
  BookOpen, Award, Printer, Save, Eye, Settings, Grid, Send,
  CreditCard, ClipboardList, GraduationCap, RefreshCw, Download
} from 'lucide-react';
import { toast } from 'sonner';
// Refactored tab components
import { 
  ExamListTab, 
  ScheduleTab, 
  MarksEntryTab, 
  AdmitCardsTab, 
  ResultsTab, 
  ExamSettingsTab 
} from '@/components/exam';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXAM_TYPES = [
  { value: 'unit_test', label: 'Unit Test' },
  { value: 'half_yearly', label: 'Half Yearly' },
  { value: 'annual', label: 'Annual' }
];

const EXAM_STATUS = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-800' },
  ongoing: { label: 'Ongoing', color: 'bg-amber-100 text-amber-800' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
  result_published: { label: 'Result Published', color: 'bg-purple-100 text-purple-800' }
};

const ExamManagementPage = () => {
  const { getAuthHeader, user } = useAuth();
  
  // If student, redirect to student-specific view
  const isStudent = user?.role === 'student';
  
  // Set default tab based on role - teacher goes to marks, admin to exams
  const isTeacherRole = user?.role === 'teacher' || user?.role === 'staff';
  const [activeTab, setActiveTab] = useState(isTeacherRole ? 'marks' : 'exams');
  const [loading, setLoading] = useState(false);
  
  // Student specific state
  const [myExamData, setMyExamData] = useState({ exams: [], total_exams: 0, results: [] });
  const [showAdmitCardDialog, setShowAdmitCardDialog] = useState(false);
  const [selectedAdmitCard, setSelectedAdmitCard] = useState(null);
  
  // Data
  const [exams, setExams] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [gradeConfig, setGradeConfig] = useState(null);
  const [mySubjects, setMySubjects] = useState([]); // Teacher's assigned subjects
  
  // Role checks
  const isAdmin = user?.role === 'school_admin' || user?.role === 'super_admin';
  const isTeacher = user?.role === 'teacher' || user?.role === 'staff';
  
  // Selected
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [classMarksView, setClassMarksView] = useState(null);
  const [marksEntries, setMarksEntries] = useState({});
  const [studentResult, setStudentResult] = useState(null);
  const [classResult, setClassResult] = useState(null);
  const [admitCards, setAdmitCards] = useState([]);
  const [seatingData, setSeatingData] = useState(null);
  
  // Dialogs
  const [examDialog, setExamDialog] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [marksDialog, setMarksDialog] = useState(false);
  const [resultDialog, setResultDialog] = useState(false);
  const [reportCardDialog, setReportCardDialog] = useState(false);
  const [admitCardDialog, setAdmitCardDialog] = useState(false);
  const [seatingDialog, setSeatingDialog] = useState(false);
  const [roomDialog, setRoomDialog] = useState(false);
  
  // Forms
  const [examForm, setExamForm] = useState({
    name: '', exam_type: 'unit_test', term: 'Term 1', academic_year: '2025-26',
    start_date: '', end_date: '', classes: [], description: ''
  });
  const [scheduleForm, setScheduleForm] = useState({
    exam_id: '', class_id: '', subject_id: '', exam_date: '',
    start_time: '09:00', end_time: '12:00', max_marks: 100,
    theory_marks: 80, practical_marks: 20, pass_marks: 33,
    room_number: ''
  });
  const [roomForm, setRoomForm] = useState({ room_number: '', building: '', capacity: 30 });

  useEffect(() => {
    if (isStudent) {
      fetchStudentExams();
      fetchStudentInfo();
    } else {
      fetchData();
    }
  }, [isStudent]);

  // Fetch student's exams and results
  const fetchStudentExams = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/exams/my-exams`, { headers: getAuthHeader() });
      setMyExamData(res.data);
    } catch (error) {
      console.error('Failed to fetch student exams:', error);
      toast.error('Failed to load your exams');
    } finally {
      setLoading(false);
    }
  };

  // Fetch student profile info for result printing
  const [studentInfo, setStudentInfo] = useState(null);
  const fetchStudentInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/students/my-profile`, { headers: getAuthHeader() });
      setStudentInfo(res.data);
    } catch (error) {
      console.error('Failed to fetch student info:', error);
    }
  };

  // Fetch teacher's assigned subjects if teacher role
  useEffect(() => {
    if (isTeacher) {
      fetchMySubjects();
    }
  }, [isTeacher]);

  const fetchMySubjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/exam-management/my-subjects`, { headers: getAuthHeader() });
      setMySubjects(res.data);
    } catch (error) {
      console.error('Failed to fetch teacher subjects:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [examsRes, classesRes, roomsRes, teachersRes, gradesRes, studentsRes, subjectsRes] = await Promise.all([
        axios.get(`${API_URL}/api/exam-management/exams`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/exam-management/rooms`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/exam-management/grade-config`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/students`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/timetable/subjects`, { headers: getAuthHeader() })
      ]);
      setExams(examsRes.data);
      setClasses(classesRes.data);
      // Use subjects from timetable/subjects API
      setSubjects(subjectsRes.data || []);
      setRooms(roomsRes.data);
      setTeachers(teachersRes.data);
      setGradeConfig(gradesRes.data);
      setStudents(studentsRes.data?.items || studentsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createExam = async () => {
    try {
      await axios.post(`${API_URL}/api/exam-management/exams`, examForm, { headers: getAuthHeader() });
      toast.success('Exam created successfully');
      setExamDialog(false);
      setExamForm({ name: '', exam_type: 'unit_test', term: 'Term 1', academic_year: '2025-26',
        start_date: '', end_date: '', classes: [], description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create exam');
    }
  };

  const fetchSchedules = async (examId) => {
    try {
      const res = await axios.get(`${API_URL}/api/exam-management/schedules?exam_id=${examId}`, { headers: getAuthHeader() });
      setSchedules(res.data);
      setSelectedExam(exams.find(e => e.id === examId));
    } catch (error) {
      toast.error('Failed to fetch schedules');
    }
  };

  const createSchedule = async () => {
    try {
      await axios.post(`${API_URL}/api/exam-management/schedules`, scheduleForm, { headers: getAuthHeader() });
      toast.success('Schedule created');
      setScheduleDialog(false);
      if (selectedExam) fetchSchedules(selectedExam.id);
    } catch (error) {
      toast.error('Failed to create schedule');
    }
  };

  const fetchMarksForClass = async (scheduleId) => {
    try {
      const res = await axios.get(`${API_URL}/api/exam-management/marks/class-view/${scheduleId}`, { headers: getAuthHeader() });
      
      // Get subject name and class name from schedule or lookup
      const schedule = res.data.schedule;
      const subjectObj = subjects.find(s => s.id === schedule?.subject_id);
      const classObj = classes.find(c => c.id === schedule?.class_id);
      
      // Store schedule_id and additional info
      setClassMarksView({ 
        ...res.data, 
        schedule_id: scheduleId,
        subject_name: subjectObj?.name || schedule?.subject_name || 'Subject',
        class_name: classObj?.name || schedule?.class_name || 'Class',
        theory_marks: schedule?.theory_marks || 80,
        practical_marks: schedule?.practical_marks || 20,
        max_marks: schedule?.max_marks || 100
      });
      
      // Initialize marks entries with existing data
      const initialEntries = {};
      res.data.students?.forEach(s => {
        initialEntries[s.student_id] = { 
          theory: s.theory_marks || '', 
          practical: s.practical_marks || '' 
        };
      });
      setMarksEntries(initialEntries);
      setMarksDialog(true);
    } catch (error) {
      toast.error('Failed to fetch marks');
    }
  };

  const saveMarks = async () => {
    try {
      const marks = Object.entries(marksEntries).map(([student_id, m]) => ({
        student_id,
        theory_marks: parseFloat(m.theory) || 0,
        practical_marks: parseFloat(m.practical) || 0
      }));
      await axios.post(`${API_URL}/api/exam-management/marks/bulk`, {
        schedule_id: classMarksView.schedule_id,
        entries: marks
      }, { headers: getAuthHeader() });
      toast.success('Marks saved');
      setMarksDialog(false);
    } catch (error) {
      toast.error('Failed to save marks');
    }
  };

  const fetchClassResult = async (examId, classId) => {
    try {
      const res = await axios.get(`${API_URL}/api/exam-management/result/${examId}/class/${classId}`, { headers: getAuthHeader() });
      
      // Get school info
      let schoolInfo = { name: 'School Name', address: '', logo_url: '' };
      try {
        const schoolRes = await axios.get(`${API_URL}/api/schools/current`, { headers: getAuthHeader() });
        schoolInfo = schoolRes.data || schoolInfo;
      } catch (e) {}
      
      // Get class name
      const classObj = classes.find(c => c.id === classId);
      const examObj = exams.find(e => e.id === examId);
      
      // Enhance result data with additional info
      const enhancedData = {
        ...res.data,
        school_name: schoolInfo.name,
        school_address: schoolInfo.address,
        school_logo: schoolInfo.logo_url,
        class_name: classObj?.name || '',
        exam_name: examObj?.name || ''
      };
      
      setClassResult(enhancedData);
    } catch (error) {
      toast.error('Failed to fetch results');
    }
  };

  const publishResult = async (examId) => {
    try {
      await axios.patch(`${API_URL}/api/exam-management/exams/${examId}/status`, { status: 'result_published' }, { headers: getAuthHeader() });
      toast.success('Results published');
      fetchData();
    } catch (error) {
      toast.error('Failed to publish');
    }
  };

  const generateAdmitCards = async (examId, classId, forceRegenerate = false) => {
    try {
      // First check if admit cards already exist (unless force regenerate)
      if (!forceRegenerate) {
        const existingRes = await axios.get(
          `${API_URL}/api/exam-management/admit-cards/${examId}/${classId}`, 
          { headers: getAuthHeader() }
        );
        
        if (existingRes.data && existingRes.data.length > 0) {
          // Ask user if they want to regenerate
          const shouldRegenerate = window.confirm(
            `${existingRes.data.length} admit cards already exist for this exam.\n\nDo you want to regenerate them?\n\nClick OK to regenerate, Cancel to view existing cards.`
          );
          
          if (!shouldRegenerate) {
            setAdmitCards(existingRes.data);
            setAdmitCardDialog(true);
            toast.success(`Loaded ${existingRes.data.length} existing admit cards`);
            return;
          }
        }
      }
      
      // Generate new admit cards
      // Get students for the class
      const studentsRes = await axios.get(`${API_URL}/api/students?class_id=${classId}&page_size=500`, { headers: getAuthHeader() });
      const allStudents = studentsRes.data?.items || studentsRes.data || [];
      
      // Remove duplicate students by student ID
      const seenIds = new Set();
      const classStudents = allStudents.filter(student => {
        const id = student.id || student.student_id;
        if (seenIds.has(id)) {
          return false;
        }
        seenIds.add(id);
        return true;
      });
      
      if (classStudents.length !== allStudents.length) {
        console.log(`Removed ${allStudents.length - classStudents.length} duplicate students`);
      }
      
      // Get exam details
      const examDetails = exams.find(e => e.id === examId);
      
      // Get school info
      let schoolInfo = { name: 'School Name', address: '', logo: '' };
      try {
        const schoolRes = await axios.get(`${API_URL}/api/schools/current`, { headers: getAuthHeader() });
        schoolInfo = schoolRes.data || schoolInfo;
      } catch (e) {
        console.log('Could not fetch school info');
      }
      
      // Get schedules for this exam
      const schedulesRes = await axios.get(`${API_URL}/api/exam-management/schedules?exam_id=${examId}&class_id=${classId}`, { headers: getAuthHeader() });
      const examSchedules = schedulesRes.data;
      
      // Generate admit cards with proper structure
      const cards = classStudents.map((student, idx) => ({
        admit_card_number: `AC-${examId.slice(0,4)}-${String(idx + 1).padStart(4, '0')}`,
        student_id: student.id,
        student_name: student.name,
        roll_number: student.student_id || student.roll_number,
        class_name: student.class_name,
        father_name: student.father_name || student.guardian_name || '',
        exam_name: examDetails?.name || 'Exam',
        exam_id: examId,
        school_name: schoolInfo.name,
        school_address: schoolInfo.address || '',
        school_logo: schoolInfo.logo_url || schoolInfo.logo || '',
        schedule: examSchedules.map(s => ({
          subject_name: s.subject_name || s.subject_id,
          exam_date: s.exam_date,
          start_time: s.start_time,
          end_time: s.end_time,
          room_number: s.room_number || ''
        }))
      }));
      
      // Save to database
      await axios.post(
        `${API_URL}/api/exam-management/admit-cards/bulk-save`,
        { exam_id: examId, class_id: classId, cards },
        { headers: getAuthHeader() }
      );
      
      setAdmitCards(cards);
      setAdmitCardDialog(true);
      toast.success(`Generated and saved ${cards.length} admit cards`);
    } catch (error) {
      console.error('Admit card error:', error);
      toast.error('Failed to generate admit cards');
    }
  };

  const printAdmitCard = (card) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Admit Card - ${card.student_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .card { border: 2px solid #333; padding: 20px; max-width: 650px; margin: auto; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 15px; }
            .school-logo { max-height: 60px; margin-bottom: 8px; }
            .school-name { font-size: 24px; font-weight: bold; color: #1a365d; margin: 5px 0; }
            .school-address { font-size: 12px; color: #666; margin-bottom: 10px; }
            .title { font-size: 18px; background: #1a365d; color: white; padding: 8px; margin: 10px 0; text-transform: uppercase; letter-spacing: 2px; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 15px 0; }
            .info-item { padding: 5px 0; }
            .label { font-weight: bold; color: #666; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            th, td { border: 1px solid #333; padding: 8px; text-align: left; }
            th { background: #f0f0f0; }
            .instructions { background: #fff8e1; padding: 10px; margin: 15px 0; border-left: 4px solid #ffc107; font-size: 12px; }
            .instructions-title { font-weight: bold; margin-bottom: 5px; }
            .footer { margin-top: 30px; display: flex; justify-content: flex-end; }
            .signature { text-align: center; }
            .signature-line { border-top: 1px solid #333; width: 180px; margin-top: 50px; }
            .signature-label { margin-top: 5px; font-size: 12px; }
            @media print { body { -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <div class="card">
            <div class="header">
              ${card.school_logo ? `<img src="${card.school_logo}" class="school-logo" alt="School Logo" />` : ''}
              <div class="school-name">${card.school_name || 'School Name'}</div>
              ${card.school_address ? `<div class="school-address">${card.school_address}</div>` : ''}
              <div class="title">Admit Card</div>
            </div>
            <div class="info-grid">
              <div class="info-item"><span class="label">Admit Card No:</span> ${card.admit_card_number}</div>
              <div class="info-item"><span class="label">Exam:</span> ${card.exam_name}</div>
              <div class="info-item"><span class="label">Student Name:</span> ${card.student_name}</div>
              <div class="info-item"><span class="label">Roll No:</span> ${card.roll_number}</div>
              <div class="info-item"><span class="label">Class:</span> ${card.class_name}</div>
              <div class="info-item"><span class="label">Father's Name:</span> ${card.father_name || '-'}</div>
            </div>
            <table>
              <tr><th>Subject</th><th>Date</th><th>Time</th><th>Room</th></tr>
              ${(card.schedule || []).map(s => `
                <tr>
                  <td>${s.subject_name}</td>
                  <td>${s.exam_date}</td>
                  <td>${s.start_time} - ${s.end_time}</td>
                  <td>${s.room_number || '-'}</td>
                </tr>
              `).join('')}
            </table>
            <div class="instructions">
              <div class="instructions-title">Important Instructions:</div>
              <ul style="margin: 5px 0; padding-left: 20px;">
                <li>Bring this admit card to the examination hall.</li>
                <li>Students without admit card will not be allowed.</li>
                <li>Reach the examination hall 15 minutes before the exam.</li>
              </ul>
            </div>
            <div class="footer">
              <div class="signature">
                <div class="signature-line"></div>
                <div class="signature-label">Principal's Signature & Seal</div>
              </div>
            </div>
          </div>
          <script>window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const printReportCard = async (result) => {
    try {
      // Fetch complete student result with subject-wise breakdown
      let fullResult = result;
      
      if (result.student_id && selectedExam?.id) {
        const res = await axios.get(
          `${API_URL}/api/exam-management/result/${selectedExam.id}/student/${result.student_id}`, 
          { headers: getAuthHeader() }
        );
        
        // Merge with passed data (school info, class name etc)
        fullResult = {
          ...result,
          student_name: res.data.student?.name || result.student_name || result.name,
          roll_number: res.data.student?.roll_number || result.roll_number,
          class_name: res.data.student?.class_name || result.class_name,
          father_name: res.data.student?.father_name || '',
          exam_name: res.data.exam?.name || result.exam_name,
          academic_year: res.data.exam?.academic_year || '2025-26',
          subjects: (res.data.subjects || []).map(s => ({
            subject_name: s.subject_name,
            max_marks: s.max_marks,
            obtained_marks: s.total_marks || 0,
            grade: s.grade || '-',
            status: s.is_passed ? 'Pass' : (s.is_absent ? 'Absent' : 'Fail')
          })),
          total_obtained: res.data.summary?.total_obtained || result.total_obtained || 0,
          total_max_marks: res.data.summary?.total_max || result.total_max || 0,
          percentage: res.data.summary?.overall_percentage || result.percentage || 0,
          overall_grade: res.data.summary?.overall_grade || result.grade || '-',
          status: res.data.summary?.result || result.result || '-',
          rank: res.data.summary?.rank || result.rank || '-'
        };
      }
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Report Card - ${fullResult.student_name || fullResult.name}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .card { border: 2px solid #333; padding: 20px; max-width: 700px; margin: auto; }
              .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 15px; }
              .school-logo { max-height: 60px; margin-bottom: 8px; }
              .school-name { font-size: 24px; font-weight: bold; color: #1a365d; margin: 5px 0; }
              .school-address { font-size: 12px; color: #666; margin-bottom: 10px; }
              .title { font-size: 18px; background: #1a365d; color: white; padding: 8px; margin: 10px 0; text-transform: uppercase; letter-spacing: 2px; }
              .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 15px 0; padding: 10px; background: #f9f9f9; }
              table { width: 100%; border-collapse: collapse; margin: 15px 0; }
              th, td { border: 1px solid #333; padding: 10px; text-align: center; }
              th { background: #1a365d; color: white; }
              .pass { color: green; font-weight: bold; }
              .fail { color: red; font-weight: bold; }
              .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 20px 0; text-align: center; }
              .summary-item { padding: 15px; background: #f0f0f0; border-radius: 8px; }
              .summary-value { font-size: 24px; font-weight: bold; color: #1a365d; }
              .grade-box { font-size: 36px; font-weight: bold; color: white; background: #1a365d; padding: 20px; border-radius: 10px; }
              .footer { margin-top: 30px; display: flex; justify-content: space-between; }
              .signature-line { border-top: 1px solid #333; width: 120px; margin-top: 50px; text-align: center; padding-top: 5px; }
              @media print { body { -webkit-print-color-adjust: exact; } }
            </style>
          </head>
          <body>
            <div class="card">
              <div class="header">
                ${fullResult.school_logo ? `<img src="${fullResult.school_logo}" class="school-logo" alt="School Logo" />` : ''}
                <div class="school-name">${fullResult.school_name || 'School Name'}</div>
                ${fullResult.school_address ? `<div class="school-address">${fullResult.school_address}</div>` : ''}
                <div class="title">REPORT CARD - ${fullResult.exam_name || ''}</div>
              </div>
              <div class="info-grid">
                <div><strong>Name:</strong> ${fullResult.student_name || fullResult.name || '-'}</div>
                <div><strong>Roll No:</strong> ${fullResult.roll_number || '-'}</div>
                <div><strong>Class:</strong> ${fullResult.class_name || '-'}</div>
                <div><strong>Academic Year:</strong> ${fullResult.academic_year || '2025-26'}</div>
              </div>
              <table>
                <tr>
                  <th>Subject</th>
                  <th>Max Marks</th>
                  <th>Marks Obtained</th>
                  <th>Grade</th>
                  <th>Status</th>
                </tr>
                ${(fullResult.subjects || []).length > 0 ? (fullResult.subjects || []).map(s => `
                  <tr>
                    <td style="text-align:left">${s.subject_name}</td>
                    <td>${s.max_marks || '-'}</td>
                    <td>${s.obtained_marks || 0}</td>
                    <td><strong>${s.grade || '-'}</strong></td>
                    <td class="${s.status === 'Pass' ? 'pass' : 'fail'}">${s.status || '-'}</td>
                  </tr>
                `).join('') : '<tr><td colspan="5" style="text-align:center">No subject marks available</td></tr>'}
                <tr style="background:#f0f0f0; font-weight:bold">
                  <td style="text-align:left">TOTAL</td>
                  <td>${fullResult.total_max_marks || '-'}</td>
                  <td>${fullResult.total_obtained || 0}</td>
                  <td colspan="2"></td>
                </tr>
              </table>
              <div class="summary">
                <div class="summary-item">
                  <div>Percentage</div>
                  <div class="summary-value">${(fullResult.percentage || 0).toFixed(1)}%</div>
                </div>
                <div class="summary-item">
                  <div>Class Rank</div>
                  <div class="summary-value">#${fullResult.rank || '-'}</div>
                </div>
                <div class="summary-item">
                  <div>Overall Grade</div>
                  <div class="grade-box">${fullResult.overall_grade || '-'}</div>
                </div>
                <div class="summary-item">
                  <div>Result</div>
                  <div class="summary-value ${(fullResult.status === 'PASS' || fullResult.status === 'Pass') ? 'pass' : 'fail'}">${fullResult.status || '-'}</div>
                </div>
              </div>
              <div class="footer">
                <div class="signature-line">Class Teacher</div>
                <div class="signature-line">Parent's Sign</div>
                <div class="signature-line">Principal</div>
              </div>
            </div>
            <script>window.print();</script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      console.error('Error printing report card:', error);
      toast.error('Failed to generate report card');
    }
  };

  const addRoom = async () => {
    try {
      await axios.post(`${API_URL}/api/exam-management/rooms`, roomForm, { headers: getAuthHeader() });
      toast.success('Room added');
      setRoomDialog(false);
      setRoomForm({ room_number: '', building: '', capacity: 30 });
      fetchData();
    } catch (error) {
      toast.error('Failed to add room');
    }
  };

  const generateSeating = async (examId) => {
    try {
      const res = await axios.post(`${API_URL}/api/exam-management/seating/${examId}`, null, {
        headers: getAuthHeader()
      });
      setSeatingData(res.data);
      setSeatingDialog(true);
      toast.success('Seating arrangement generated');
    } catch (error) {
      toast.error('Failed to generate seating');
    }
  };

  // ==================== STUDENT VIEW ====================
  if (isStudent) {
    const examsWithResults = myExamData.exams?.filter(e => {
      // Check both new and legacy formats
      const hasNewResults = e.subjects && e.subjects.some(s => s.marks_obtained !== null);
      const hasLegacyResults = e.results && e.results.length > 0;
      return hasNewResults || hasLegacyResults || e.results_published;
    }) || [];
    
    const totalPercentage = examsWithResults.length > 0 
      ? (examsWithResults.reduce((sum, e) => sum + (e.percentage || 0), 0) / examsWithResults.length).toFixed(1)
      : 0;

    // Function to download admit card
    const downloadAdmitCard = async (examId) => {
      try {
        const res = await axios.get(`${API_URL}/api/exam-management/my-admit-card/${examId}`, { headers: getAuthHeader() });
        setSelectedAdmitCard(res.data);
        setShowAdmitCardDialog(true);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to load admit card');
      }
    };

    // Function to download result
    const downloadResult = (exam) => {
      const printWindow = window.open('', '_blank');
      const subjects = exam.subjects || exam.results || [];
      const subjectsHtml = subjects.map(s => `
        <tr>
          <td>${s.subject_name || s.subject || 'Subject'}</td>
          <td>${s.max_marks || 100}</td>
          <td>${s.marks_obtained ?? s.marks ?? '-'}</td>
          <td>${s.grade || '-'}</td>
          <td>${s.is_absent ? 'Absent' : (s.marks_obtained !== null ? (s.marks_obtained >= (s.passing_marks || 33) ? 'Pass' : 'Fail') : '-')}</td>
        </tr>
      `).join('');

      // Get school info from studentInfo or user
      const schoolName = studentInfo?.school_name || user?.school_name || 'Delhi Public School';
      const schoolAddress = studentInfo?.school_address || user?.school_address || '';
      const schoolLogo = studentInfo?.school_logo || user?.school_logo || '';
      const studentName = studentInfo?.name || user?.name || 'Student';
      const className = studentInfo?.class_name || user?.class_name || '';
      const rollNumber = studentInfo?.roll_number || studentInfo?.student_id || user?.roll_number || '';
      const fatherName = studentInfo?.father_name || '';

      // Get academic year from exam date
      const examDate = exam.start_date || exam.exam_date || '';
      const examYear = examDate ? new Date(examDate).getFullYear() : new Date().getFullYear();
      const academicYear = `${examYear - 1}-${examYear.toString().slice(-2)}`;

      printWindow.document.write(`
        <html>
          <head>
            <title>Exam Result - ${exam.name}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 30px; max-width: 800px; margin: 0 auto; }
              .school-header { text-align: center; margin-bottom: 20px; }
              .school-header img { height: 80px; margin-bottom: 10px; }
              .school-header h1 { margin: 0; color: #1a365d; font-size: 24px; }
              .school-header p { margin: 5px 0; color: #555; font-size: 12px; }
              .result-title { text-align: center; background: #1a365d; color: white; padding: 10px; margin: 20px 0; }
              .result-title h2 { margin: 0; font-size: 18px; }
              .result-title p { margin: 5px 0 0 0; font-size: 12px; }
              .student-info { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 20px 0; padding: 15px; background: #f8f9fa; border: 1px solid #ddd; }
              .info-item { font-size: 13px; }
              .info-label { font-weight: bold; color: #333; }
              table { width: 100%; border-collapse: collapse; margin: 20px 0; }
              th, td { padding: 10px; text-align: center; border: 1px solid #333; font-size: 13px; }
              th { background: #e2e8f0; font-weight: bold; }
              .total-row { font-weight: bold; background: #e2e8f0; }
              .summary { margin-top: 20px; padding: 15px; background: #d4edda; border: 1px solid #c3e6cb; }
              .summary h4 { margin: 0 0 10px 0; color: #155724; }
              .pass { color: #155724; font-weight: bold; }
              .fail { color: #721c24; font-weight: bold; }
              .signatures { display: flex; justify-content: space-between; margin-top: 50px; }
              .signature-box { text-align: center; width: 150px; }
              .signature-line { border-top: 1px solid #333; padding-top: 5px; font-size: 12px; }
              .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
              @media print { body { padding: 15px; } }
            </style>
          </head>
          <body>
            <div class="school-header">
              ${schoolLogo ? `<img src="${schoolLogo}" alt="School Logo" />` : ''}
              <h1>${schoolName}</h1>
              <p>${schoolAddress}</p>
            </div>

            <div class="result-title">
              <h2>EXAMINATION RESULT</h2>
              <p>${exam.name} | ${exam.exam_type || 'Examination'} | Academic Year: ${academicYear}</p>
            </div>
            
            <div class="student-info">
              <div class="info-item"><span class="info-label">Student Name:</span> ${studentName}</div>
              <div class="info-item"><span class="info-label">Class:</span> ${className}</div>
              <div class="info-item"><span class="info-label">Roll No:</span> ${rollNumber}</div>
              <div class="info-item"><span class="info-label">Father's Name:</span> ${fatherName}</div>
              <div class="info-item"><span class="info-label">Exam Date:</span> ${examDate}</div>
              <div class="info-item"><span class="info-label">Result Date:</span> ${new Date().toLocaleDateString('en-IN')}</div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Max Marks</th>
                  <th>Marks Obtained</th>
                  <th>Grade</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                ${subjectsHtml}
                <tr class="total-row">
                  <td><strong>Total</strong></td>
                  <td><strong>${exam.total_max || exam.max_marks || subjects.reduce((s, x) => s + (x.max_marks || 100), 0)}</strong></td>
                  <td><strong>${exam.total_obtained || subjects.reduce((s, x) => s + (x.marks_obtained || x.marks || 0), 0)}</strong></td>
                  <td colspan="2"><strong>Percentage: ${(exam.percentage || 0).toFixed(1)}%</strong></td>
                </tr>
              </tbody>
            </table>

            <div class="summary">
              <h4>Result Declaration</h4>
              <p><strong>Overall Percentage:</strong> ${(exam.percentage || 0).toFixed(1)}%</p>
              <p><strong>Result:</strong> <span class="${exam.percentage >= 33 ? 'pass' : 'fail'}">${exam.percentage >= 33 ? 'PASSED' : 'FAILED'}</span></p>
            </div>

            <div class="signatures">
              <div class="signature-box">
                <div class="signature-line">Class Teacher</div>
              </div>
              <div class="signature-box">
                <div class="signature-line">Exam Controller</div>
              </div>
              <div class="signature-box">
                <div class="signature-line">Principal</div>
              </div>
            </div>

            <div class="footer">
              <p>This is a computer-generated result sheet and is valid without signature.</p>
              <p>Generated on: ${new Date().toLocaleString('en-IN')}</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    };

    const printAdmitCard = () => {
      const printContent = document.getElementById('admit-card-print');
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Admit Card</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 15px; }
              .header h2 { margin: 0 0 5px 0; }
              .student-info { display: flex; justify-content: space-between; margin: 15px 0; }
              .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
              .photo-box { width: 100px; height: 120px; border: 1px solid #000; display: flex; align-items: center; justify-content: center; }
              table { width: 100%; border-collapse: collapse; margin: 15px 0; }
              th, td { padding: 8px; text-align: left; border: 1px solid #000; }
              th { background: #f0f0f0; }
              .instructions { font-size: 11px; margin-top: 15px; }
              .signature { margin-top: 30px; text-align: right; }
            </style>
          </head>
          <body>${printContent.innerHTML}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    };

    return (
      <DashboardLayout title="My Exams & Results">
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Exams</p>
                  <p className="text-2xl font-bold">{myExamData.total_exams || 0}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Award className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Results Declared</p>
                  <p className="text-2xl font-bold">{examsWithResults.length}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <GraduationCap className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Average Percentage</p>
                  <p className="text-2xl font-bold">{totalPercentage}%</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Exams List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" /> My Exams
              </CardTitle>
              <CardDescription>Your exam schedule and results</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-muted-foreground">Loading...</p>
              ) : myExamData.exams?.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No exams scheduled</p>
              ) : (
                <div className="space-y-3">
                  {myExamData.exams?.map((exam, idx) => {
                    const hasResults = (exam.subjects && exam.subjects.some(s => s.marks_obtained !== null)) || 
                      (exam.results && exam.results.length > 0) || exam.results_published;
                    const isPast = new Date(exam.start_date || exam.exam_date) < new Date();
                    
                    return (
                      <div key={idx} className="p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{exam.name || exam.exam_name}</h3>
                              {hasResults ? (
                                <Badge className="bg-green-100 text-green-800">Result Declared</Badge>
                              ) : isPast ? (
                                <Badge variant="secondary">Awaiting Result</Badge>
                              ) : (
                                <Badge className="bg-blue-100 text-blue-800">Upcoming</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {exam.exam_type} | Total: {exam.total_max || exam.max_marks || exam.total_marks || 100} marks
                            </p>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                              <Calendar className="h-4 w-4" />
                              {exam.start_date || exam.exam_date}
                              {exam.end_date && exam.end_date !== exam.start_date && ` - ${exam.end_date}`}
                            </div>
                          </div>
                          
                          <div className="text-right flex flex-col items-end gap-2">
                            {hasResults && (
                              <>
                                <p className="text-2xl font-bold text-green-600">
                                  {exam.total_obtained || exam.results?.[0]?.marks || 0}/{exam.total_max || exam.max_marks || 100}
                                </p>
                                <Badge className={exam.percentage >= 60 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                                  {(exam.percentage || 0).toFixed(1)}%
                                </Badge>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => downloadResult(exam)}
                                  data-testid={`download-result-${exam.id}`}
                                >
                                  <Download className="h-4 w-4 mr-1" />
                                  Download Result
                                </Button>
                              </>
                            )}
                            {!isPast && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => downloadAdmitCard(exam.id)}
                                data-testid={`admit-card-${exam.id}`}
                              >
                                <Printer className="h-4 w-4 mr-1" />
                                Admit Card
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Subject-wise results */}
                        {hasResults && exam.subjects && exam.subjects.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-sm font-medium mb-2">Subject-wise Marks:</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {exam.subjects.map((s, i) => (
                                <div key={i} className="text-sm bg-muted/50 p-2 rounded">
                                  <span className="text-muted-foreground">{s.subject_name}:</span>
                                  <span className="ml-1 font-medium">
                                    {s.is_absent ? 'Absent' : `${s.marks_obtained ?? '-'}/${s.max_marks}`}
                                  </span>
                                  {s.grade && <span className="ml-1 text-xs text-primary">({s.grade})</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Legacy results format */}
                        {hasResults && exam.results && exam.results.length > 0 && !exam.subjects && (
                          <div className="mt-3 pt-3 border-t">
                            <div className="flex items-center gap-4">
                              <span className="text-sm font-medium">Marks:</span>
                              <span className="text-lg font-bold text-green-600">
                                {exam.results[0].marks || exam.results[0].marks_obtained}/{exam.max_marks || 100}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Admit Card Dialog */}
        <Dialog open={showAdmitCardDialog} onOpenChange={setShowAdmitCardDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Admit Card</DialogTitle>
            </DialogHeader>
            {selectedAdmitCard && (
              <div id="admit-card-print" className="space-y-4 p-4 border rounded-lg">
                {/* School Header */}
                <div className="text-center border-b pb-4">
                  {selectedAdmitCard.school_logo && (
                    <img src={selectedAdmitCard.school_logo} alt="School" className="h-14 mx-auto mb-2" />
                  )}
                  <h2 className="text-lg font-bold">{selectedAdmitCard.school_name}</h2>
                  <p className="text-xs text-muted-foreground">{selectedAdmitCard.school_address}</p>
                  <h3 className="text-base font-semibold mt-2 bg-primary/10 py-1">ADMIT CARD</h3>
                </div>

                {/* Student Details */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{selectedAdmitCard.student_name}</span></div>
                    <div><span className="text-muted-foreground">Roll:</span> <span className="font-medium">{selectedAdmitCard.roll_number}</span></div>
                    <div><span className="text-muted-foreground">Class:</span> <span className="font-medium">{selectedAdmitCard.class_name}</span></div>
                    <div><span className="text-muted-foreground">Father:</span> <span className="font-medium">{selectedAdmitCard.father_name || '-'}</span></div>
                    <div className="col-span-2"><span className="text-muted-foreground">Exam:</span> <span className="font-semibold text-primary">{selectedAdmitCard.exam_name}</span></div>
                  </div>
                  <div className="flex justify-center">
                    {selectedAdmitCard.photo_url ? (
                      <img src={selectedAdmitCard.photo_url} alt="Photo" className="h-20 w-16 object-cover border" />
                    ) : (
                      <div className="h-20 w-16 border flex items-center justify-center text-xs text-muted-foreground">Photo</div>
                    )}
                  </div>
                </div>

                {/* Schedule */}
                <div>
                  <h4 className="font-medium mb-2 text-sm">Exam Schedule:</h4>
                  <table className="w-full text-xs border">
                    <thead className="bg-muted">
                      <tr>
                        <th className="p-1.5 text-left border">Subject</th>
                        <th className="p-1.5 text-left border">Date</th>
                        <th className="p-1.5 text-left border">Time</th>
                        <th className="p-1.5 text-left border">Room</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedAdmitCard.schedule || selectedAdmitCard.exam_schedule || []).map((s, i) => (
                        <tr key={i}>
                          <td className="p-1.5 border">{s.subject_name || s.subject}</td>
                          <td className="p-1.5 border">{s.exam_date || s.date}</td>
                          <td className="p-1.5 border">{s.time || `${s.start_time}-${s.end_time}`}</td>
                          <td className="p-1.5 border">{s.room_number || s.room || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Instructions */}
                <div className="text-xs text-muted-foreground">
                  <p className="font-medium">Instructions:</p>
                  <ul className="list-disc list-inside">
                    <li>Bring this admit card to the examination hall</li>
                    <li>Students without admit card will not be allowed</li>
                    <li>Reach 15 minutes before scheduled time</li>
                  </ul>
                </div>

                <div className="text-right text-xs mt-4 pt-2 border-t border-dashed">
                  Principal's Signature
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdmitCardDialog(false)}>Close</Button>
              <Button onClick={printAdmitCard}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    );
  }

  // ==================== ADMIN/TEACHER VIEW ====================
  return (
    <DashboardLayout title={isTeacher ? "Exam Marks Entry" : "Exam Management"}>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className={`grid w-full max-w-4xl ${isAdmin ? 'grid-cols-6' : 'grid-cols-2'}`}>
          {isAdmin && (
            <>
              <TabsTrigger value="exams" data-testid="exams-tab">
                <Calendar className="h-4 w-4 mr-1" /> Exams
              </TabsTrigger>
              <TabsTrigger value="schedule" data-testid="schedule-tab">
                <Clock className="h-4 w-4 mr-1" /> Schedule
              </TabsTrigger>
            </>
          )}
          <TabsTrigger value="marks" data-testid="marks-tab">
            <FileText className="h-4 w-4 mr-1" /> Marks Entry
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="admit" data-testid="admit-tab">
              <CreditCard className="h-4 w-4 mr-1" /> Admit Card
            </TabsTrigger>
          )}
          <TabsTrigger value="results" data-testid="results-tab">
            <Award className="h-4 w-4 mr-1" /> Results
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="settings" data-testid="settings-tab">
              <Settings className="h-4 w-4 mr-1" /> Settings
            </TabsTrigger>
          )}
        </TabsList>

        {/* ==================== EXAMS TAB ==================== */}
        <TabsContent value="exams" className="space-y-4">
          <ExamListTab 
            exams={exams}
            onCreateExam={() => setExamDialog(true)}
            onViewSchedule={(exam) => {
              setSelectedExam(exam);
              fetchSchedules(exam.id);
              setActiveTab('schedule');
            }}
            onViewAdmitCards={(exam) => {
              setSelectedExam(exam);
              setActiveTab('admit');
            }}
          />
        </TabsContent>

        {/* ==================== SCHEDULE TAB ==================== */}
        <TabsContent value="schedule" className="space-y-4">
          <ScheduleTab
            exams={exams}
            schedules={schedules}
            selectedExam={selectedExam}
            onSelectExam={(id) => fetchSchedules(id)}
            onAddSchedule={() => {
              setScheduleForm(prev => ({ ...prev, exam_id: selectedExam?.id || '' }));
              setScheduleDialog(true);
            }}
            onEnterMarks={fetchMarksForClass}
          />
        </TabsContent>

        {/* ==================== MARKS ENTRY TAB ==================== */}
        <TabsContent value="marks" className="space-y-4">
          <MarksEntryTab
            exams={exams}
            schedules={schedules}
            selectedExam={selectedExam}
            mySubjects={mySubjects}
            isAdmin={isAdmin}
            isTeacher={isTeacher}
            onSelectExam={(id) => fetchSchedules(id)}
            onEnterMarks={fetchMarksForClass}
          />
        </TabsContent>

        {/* ==================== ADMIT CARD TAB ==================== */}
        <TabsContent value="admit" className="space-y-4">
          <AdmitCardsTab
            exams={exams}
            classes={classes}
            selectedExam={selectedExam}
            selectedClass={selectedClass}
            admitCards={admitCards}
            getAuthHeader={getAuthHeader}
            onSelectExam={(id) => setSelectedExam(exams.find(e => e.id === id))}
            onSelectClass={setSelectedClass}
            onGenerateCards={() => generateAdmitCards(selectedExam?.id, selectedClass)}
            onPrintCard={printAdmitCard}
            onClearCards={() => setAdmitCards([])}
          />
        </TabsContent>

        {/* ==================== RESULTS TAB ==================== */}
        <TabsContent value="results" className="space-y-4">
          <ResultsTab
            exams={exams}
            classes={classes}
            selectedExam={selectedExam}
            selectedClass={selectedClass}
            classResult={classResult}
            onSelectExam={(id) => setSelectedExam(exams.find(e => e.id === id))}
            onSelectClass={setSelectedClass}
            onViewResults={() => fetchClassResult(selectedExam?.id, selectedClass)}
            onPublishResult={publishResult}
            onPrintReportCard={printReportCard}
          />
        </TabsContent>

        {/* ==================== SETTINGS TAB ==================== */}
        <TabsContent value="settings" className="space-y-4">
          <ExamSettingsTab
            exams={exams}
            rooms={rooms}
            gradeConfig={gradeConfig}
            selectedExam={selectedExam}
            onSelectExam={(id) => setSelectedExam(exams.find(e => e.id === id))}
            onAddRoom={() => setRoomDialog(true)}
            onGenerateSeating={generateSeating}
          />
        </TabsContent>
      </Tabs>

      {/* ==================== DIALOGS ==================== */}
      
      {/* Create Exam Dialog */}
      <Dialog open={examDialog} onOpenChange={setExamDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Create New Exam</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Exam Name *</Label><Input value={examForm.name} onChange={e => setExamForm({...examForm, name: e.target.value})} placeholder="e.g., Unit Test 1" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Exam Type</Label>
                <Select value={examForm.exam_type} onValueChange={v => setExamForm({...examForm, exam_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{EXAM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Term</Label><Input value={examForm.term} onChange={e => setExamForm({...examForm, term: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Start Date</Label><Input type="date" value={examForm.start_date} onChange={e => setExamForm({...examForm, start_date: e.target.value})} /></div>
              <div><Label>End Date</Label><Input type="date" value={examForm.end_date} onChange={e => setExamForm({...examForm, end_date: e.target.value})} /></div>
            </div>
            <div><Label>Classes</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {classes.map(c => (
                  <label key={c.id} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-muted">
                    <Checkbox checked={examForm.classes.includes(c.id)} onCheckedChange={(checked) => {
                      setExamForm({...examForm, classes: checked ? [...examForm.classes, c.id] : examForm.classes.filter(id => id !== c.id)});
                    }} />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={createExam}>Create Exam</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Schedule Dialog */}
      <Dialog open={scheduleDialog} onOpenChange={setScheduleDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Exam Schedule</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Class</Label>
                <Select value={scheduleForm.class_id} onValueChange={v => setScheduleForm({...scheduleForm, class_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select Class" /></SelectTrigger>
                  <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Subject</Label>
                <Select value={scheduleForm.subject_id} onValueChange={v => setScheduleForm({...scheduleForm, subject_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select Subject" /></SelectTrigger>
                  <SelectContent>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Exam Date</Label><Input type="date" value={scheduleForm.exam_date} onChange={e => setScheduleForm({...scheduleForm, exam_date: e.target.value})} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Start Time</Label><Input type="time" value={scheduleForm.start_time} onChange={e => setScheduleForm({...scheduleForm, start_time: e.target.value})} /></div>
              <div><Label>End Time</Label><Input type="time" value={scheduleForm.end_time} onChange={e => setScheduleForm({...scheduleForm, end_time: e.target.value})} /></div>
              <div>
                <Label>Exam Room</Label>
                <Select value={scheduleForm.room_number} onValueChange={v => setScheduleForm({...scheduleForm, room_number: v})}>
                  <SelectTrigger><SelectValue placeholder="Select Room" /></SelectTrigger>
                  <SelectContent>
                    {rooms.map(r => (
                      <SelectItem key={r.id || r.room_number} value={r.room_number}>
                        {r.room_number} {r.building ? `(${r.building})` : ''} - Cap: {r.capacity}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Max Marks</Label><Input type="number" value={scheduleForm.max_marks} onChange={e => setScheduleForm({...scheduleForm, max_marks: parseInt(e.target.value)})} /></div>
              <div><Label>Theory</Label><Input type="number" value={scheduleForm.theory_marks} onChange={e => setScheduleForm({...scheduleForm, theory_marks: parseInt(e.target.value)})} /></div>
              <div><Label>Practical</Label><Input type="number" value={scheduleForm.practical_marks} onChange={e => setScheduleForm({...scheduleForm, practical_marks: parseInt(e.target.value)})} /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={createSchedule}>Add Schedule</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Marks Entry Dialog */}
      <Dialog open={marksDialog} onOpenChange={setMarksDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enter Marks - {classMarksView?.subject_name || classMarksView?.schedule?.subject_name}</DialogTitle>
            <p className="text-sm text-muted-foreground">{classMarksView?.class_name} • Max: {classMarksView?.schedule?.max_marks || classMarksView?.max_marks}</p>
          </DialogHeader>
          
          {/* CSV Upload Section */}
          <div className="bg-muted/30 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">Bulk Upload via CSV</h4>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    const scheduleId = classMarksView?.schedule_id || classMarksView?.schedule?.id;
                    try {
                      const response = await axios.get(
                        `${API_URL}/api/exam-management/marks/template/${scheduleId}?format=csv`,
                        { headers: getAuthHeader(), responseType: 'blob' }
                      );
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `marks_template.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    } catch (error) {
                      toast.error('Download failed');
                    }
                  }}
                  data-testid="download-template-btn"
                >
                  <Download className="h-4 w-4 mr-1" />
                  CSV
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    const scheduleId = classMarksView?.schedule_id || classMarksView?.schedule?.id;
                    try {
                      const response = await axios.get(
                        `${API_URL}/api/exam-management/marks/template/${scheduleId}?format=excel`,
                        { headers: getAuthHeader(), responseType: 'blob' }
                      );
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `marks_template.xlsx`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    } catch (error) {
                      toast.error('Download failed');
                    }
                  }}
                  data-testid="download-excel-template-btn"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Excel
                </Button>
                <label className="cursor-pointer">
                  <Button variant="secondary" size="sm" asChild>
                    <span>
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          const scheduleId = classMarksView?.schedule_id || classMarksView?.schedule?.id;
                          try {
                            const res = await axios.post(
                              `${API_URL}/api/exam-management/marks/upload/${scheduleId}`,
                              formData,
                              { 
                                headers: { 
                                  ...getAuthHeader(),
                                  'Content-Type': 'multipart/form-data'
                                }
                              }
                            );
                            toast.success(res.data.message);
                            if (res.data.errors?.length > 0) {
                              res.data.errors.forEach(err => toast.error(err));
                            }
                            // Refresh marks view
                            const viewRes = await axios.get(
                              `${API_URL}/api/exam-management/marks/class-view/${scheduleId}`,
                              { headers: getAuthHeader() }
                            );
                            setClassMarksView(viewRes.data);
                            // Pre-fill entries
                            const entries = {};
                            viewRes.data.students.forEach(s => {
                              if (s.theory_marks || s.practical_marks) {
                                entries[s.student_id] = {
                                  theory: s.theory_marks ?? '',
                                  practical: s.practical_marks ?? ''
                                };
                              }
                            });
                            setMarksEntries(entries);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Upload failed');
                          }
                          e.target.value = ''; // Reset input
                        }}
                        data-testid="upload-csv-input"
                      />
                      Upload File
                    </span>
                  </Button>
                </label>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Download CSV or Excel template, fill in marks, and upload. Student IDs are pre-filled - just enter the marks!
            </p>
          </div>

          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-2 text-left">Roll</th>
                <th className="p-2 text-left">Student Name</th>
                <th className="p-2 text-left">Theory ({classMarksView?.schedule?.theory_marks || classMarksView?.theory_marks || 80})</th>
                <th className="p-2 text-left">Practical ({classMarksView?.schedule?.practical_marks || classMarksView?.practical_marks || 20})</th>
                <th className="p-2 text-left">Total</th>
              </tr>
            </thead>
            <tbody>
              {classMarksView?.students?.map(s => {
                const theoryMax = classMarksView?.schedule?.theory_marks || classMarksView?.theory_marks || 80;
                const practicalMax = classMarksView?.schedule?.practical_marks || classMarksView?.practical_marks || 20;
                const theoryVal = parseFloat(marksEntries[s.student_id]?.theory || 0);
                const practicalVal = parseFloat(marksEntries[s.student_id]?.practical || 0);
                const total = theoryVal + practicalVal;
                const isTheoryOverflow = theoryVal > theoryMax;
                const isPracticalOverflow = practicalVal > practicalMax;
                
                return (
                <tr key={s.student_id} className="border-t">
                  <td className="p-2">{s.roll_number}</td>
                  <td className="p-2">{s.name || s.student_name}</td>
                  <td className="p-2">
                    <Input 
                      type="number" 
                      className={`w-20 ${isTheoryOverflow ? 'border-red-500 bg-red-50' : ''}`}
                      min={0}
                      max={theoryMax}
                      value={marksEntries[s.student_id]?.theory || ''}
                      onChange={e => {
                        const val = parseFloat(e.target.value) || 0;
                        if (val > theoryMax) {
                          toast.error(`Theory marks cannot exceed ${theoryMax}`);
                          return;
                        }
                        setMarksEntries({...marksEntries, [s.student_id]: {...marksEntries[s.student_id], theory: e.target.value}});
                      }}
                    />
                    {isTheoryOverflow && <span className="text-xs text-red-500">Max: {theoryMax}</span>}
                  </td>
                  <td className="p-2">
                    <Input 
                      type="number" 
                      className={`w-20 ${isPracticalOverflow ? 'border-red-500 bg-red-50' : ''}`}
                      min={0}
                      max={practicalMax}
                      value={marksEntries[s.student_id]?.practical || ''}
                      onChange={e => {
                        const val = parseFloat(e.target.value) || 0;
                        if (val > practicalMax) {
                          toast.error(`Practical marks cannot exceed ${practicalMax}`);
                          return;
                        }
                        setMarksEntries({...marksEntries, [s.student_id]: {...marksEntries[s.student_id], practical: e.target.value}});
                      }}
                    />
                    {isPracticalOverflow && <span className="text-xs text-red-500">Max: {practicalMax}</span>}
                  </td>
                  <td className="p-2 font-bold">
                    <span className={total > (theoryMax + practicalMax) ? 'text-red-500' : ''}>{total}</span>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
          <DialogFooter><Button onClick={saveMarks}><Save className="h-4 w-4 mr-2" /> Save Marks</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Room Dialog */}
      <Dialog open={roomDialog} onOpenChange={setRoomDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Exam Room</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Room Number *</Label><Input value={roomForm.room_number} onChange={e => setRoomForm({...roomForm, room_number: e.target.value})} placeholder="e.g., Hall-A" /></div>
            <div><Label>Building</Label><Input value={roomForm.building} onChange={e => setRoomForm({...roomForm, building: e.target.value})} /></div>
            <div><Label>Capacity</Label><Input type="number" value={roomForm.capacity} onChange={e => setRoomForm({...roomForm, capacity: parseInt(e.target.value)})} /></div>
          </div>
          <DialogFooter><Button onClick={addRoom}>Add Room</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seating Dialog */}
      <Dialog open={seatingDialog} onOpenChange={setSeatingDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Seating Arrangement</DialogTitle></DialogHeader>
          {seatingData && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {seatingData.rooms?.map(room => (
                <Card key={room.room_number}>
                  <CardHeader className="py-2"><CardTitle className="text-sm">{room.room_number} ({room.students?.length} students)</CardTitle></CardHeader>
                  <CardContent className="py-2">
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      {room.students?.map((s, idx) => (
                        <div key={idx} className="p-1 bg-muted rounded text-center">
                          <div className="font-medium">{s.roll_number}</div>
                          <div className="text-muted-foreground truncate">{s.student_name}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ExamManagementPage;
